import type { AnyFieldApi, AnyFormApi } from '@tanstack/react-form';

import type { BorrowerAddressData } from '../../../types';

export const SAME_AS_PRIMARY_FIELD =
  'residenceHistoryDetails.mailingAddress.sameAsPrimary';

const MAILING_ADDRESS_FIELD = 'residenceHistoryDetails.mailingAddress';
const PRIMARY_RESIDENCE_FIELD =
  'residenceHistoryDetails.primaryResidence.borrowerAddress.primaryResidence';

function getLinkedAddressFieldsFromCountryField(fieldName: string): {
  stateFieldPath: string;
  zipCodeFieldPath: string;
} | null {
  if (fieldName === 'residenceHistoryDetails.mailingAddress.country') {
    return {
      stateFieldPath: 'residenceHistoryDetails.mailingAddress.state',
      zipCodeFieldPath: 'residenceHistoryDetails.mailingAddress.zipCode',
    };
  }

  if (fieldName === 'residenceHistoryDetails.primaryResidence.country') {
    return {
      stateFieldPath:
        'residenceHistoryDetails.primaryResidence.borrowerAddress.state',
      zipCodeFieldPath:
        'residenceHistoryDetails.primaryResidence.borrowerAddress.zipCode',
    };
  }

  if (
    fieldName.startsWith('residenceHistoryDetails.borrowerResidenceHistory[') ||
    fieldName.startsWith('residenceHistoryDetails.borrowerAssociatedAddresses[')
  ) {
    return {
      stateFieldPath: fieldName.replace(/\.country$/, '.borrowerAddress.state'),
      zipCodeFieldPath: fieldName.replace(
        /\.country$/,
        '.borrowerAddress.zipCode',
      ),
    };
  }

  return null;
}

function getCountryFieldFromAddressField(fieldName: string): string | null {
  if (fieldName.startsWith('residenceHistoryDetails.mailingAddress.')) {
    return 'residenceHistoryDetails.mailingAddress.country';
  }

  if (fieldName.includes('.borrowerAddress.')) {
    return fieldName.replace(/\.borrowerAddress\.[^.]+$/, '.country');
  }

  return null;
}

function setFieldValueIfChanged(
  formApi: AnyFormApi,
  fieldName: string,
  value: unknown,
) {
  const currentValue = formApi.getFieldValue(fieldName);

  if (currentValue !== value) {
    formApi.setFieldValue(fieldName, value);
  }
}

function formatZipCodeInput(value: string, country?: string): string {
  if (country === 'US') {
    const digitsOnly = value.replaceAll(/\D/g, '').slice(0, 9);

    if (digitsOnly.length <= 5) {
      return digitsOnly;
    }

    return `${digitsOnly.slice(0, 5)}-${digitsOnly.slice(5)}`;
  }

  return value.slice(0, 15);
}

export function syncMailingAddressWithPrimary(formApi: AnyFormApi) {
  const primaryResidence = formApi.getFieldValue(
    'residenceHistoryDetails.primaryResidence',
  ) as BorrowerAddressData | null | undefined;

  const borrowerAddresses = formApi.getFieldValue(
    'residenceHistoryDetails.borrowerResidenceHistory',
  ) as BorrowerAddressData[];

  const primaryAddress =
    primaryResidence ??
    borrowerAddresses?.find(
      (address) => address.borrowerAddress?.primaryResidence,
    );

  formApi.setFieldValue(MAILING_ADDRESS_FIELD, {
    ...formApi.getFieldValue(MAILING_ADDRESS_FIELD),
    addressLine1: primaryAddress?.borrowerAddress?.addressLine1 ?? '',
    city: primaryAddress?.borrowerAddress?.city ?? '',
    state: primaryAddress?.borrowerAddress?.state ?? '',
    zipCode: primaryAddress?.borrowerAddress?.zipCode ?? '',
    country: primaryAddress?.country ?? '',
    unit: primaryAddress?.borrowerAddress?.unit ?? '',
  });
}

function isPrimaryAddressField(
  fieldName: string,
  isPrimaryAddressSelectionEnabled: boolean,
): boolean {
  if (
    isPrimaryAddressSelectionEnabled &&
    fieldName === 'residenceHistoryDetails.primaryResidence'
  ) {
    return true;
  }

  if (
    isPrimaryAddressSelectionEnabled &&
    fieldName.startsWith('residenceHistoryDetails.primaryResidence.')
  ) {
    return /(\.country|\.borrowerAddress\.(primaryResidence|addressLine1|city|state|zipCode|unit))$/.test(
      fieldName,
    );
  }

  if (
    !fieldName.startsWith('residenceHistoryDetails.borrowerResidenceHistory[')
  ) {
    return false;
  }

  return /(\.country|\.borrowerAddress\.(primaryResidence|addressLine1|city|state|zipCode|unit))$/.test(
    fieldName,
  );
}

export function addressCountryListener(
  fieldApi: AnyFieldApi,
  formApi: AnyFormApi,
) {
  const linkedFields = getLinkedAddressFieldsFromCountryField(fieldApi.name);

  if (!linkedFields) {
    return;
  }

  setFieldValueIfChanged(formApi, linkedFields.stateFieldPath, '');
  setFieldValueIfChanged(formApi, linkedFields.zipCodeFieldPath, '');
}

export function addressStateListener(
  fieldApi: AnyFieldApi,
  formApi: AnyFormApi,
) {
  const countryFieldPath = getCountryFieldFromAddressField(fieldApi.name);

  const country = countryFieldPath
    ? (formApi.getFieldValue(countryFieldPath) as string)
    : undefined;

  if (country === 'US' || typeof fieldApi.state.value !== 'string') {
    return;
  }

  const sanitizedStateValue = fieldApi.state.value
    .replaceAll(/[^a-zA-Z]/g, '')
    .slice(0, 2);

  setFieldValueIfChanged(formApi, fieldApi.name, sanitizedStateValue);
}

export function addressZipCodeListener(
  fieldApi: AnyFieldApi,
  formApi: AnyFormApi,
) {
  if (typeof fieldApi.state.value !== 'string') {
    return;
  }

  const countryFieldPath = getCountryFieldFromAddressField(fieldApi.name);

  const country = countryFieldPath
    ? (formApi.getFieldValue(countryFieldPath) as string)
    : undefined;

  const formattedZipCode = formatZipCodeInput(fieldApi.state.value, country);

  setFieldValueIfChanged(formApi, fieldApi.name, formattedZipCode);
}

export function sameAsPrimaryListener(
  fieldApi: AnyFieldApi,
  formApi: AnyFormApi,
) {
  const sameAsPrimary: boolean = fieldApi.state.value;

  if (sameAsPrimary) {
    syncMailingAddressWithPrimary(formApi);
    formApi.validateAllFields('blur');

    return;
  }

  formApi.setFieldValue(MAILING_ADDRESS_FIELD, {
    ...formApi.getFieldValue(MAILING_ADDRESS_FIELD),
    addressLine1: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    unit: '',
  });
}

export function primaryResidencePreviousListener(
  fieldApi: AnyFieldApi,
  formApi: AnyFormApi,
) {
  if (
    fieldApi.name !== PRIMARY_RESIDENCE_FIELD ||
    (fieldApi.state.value !== false && fieldApi.state.value !== 'false')
  ) {
    return;
  }

  formApi.setFieldValue('residenceHistoryDetails', (currentDetails) => {
    const primaryResidence = currentDetails.primaryResidence;

    if (!primaryResidence) {
      return currentDetails;
    }

    return {
      ...currentDetails,
      primaryResidence: null,
      primaryResidenceSelectionId: '',
      borrowerResidenceHistory: [
        ...(currentDetails.borrowerResidenceHistory ?? []),
        {
          ...primaryResidence,
          borrowerAddress: {
            ...primaryResidence.borrowerAddress,
            primaryResidence: false,
          },
        },
      ],
    };
  });
}

export function syncMailingAddressFromPrimaryListener(
  fieldApi: AnyFieldApi,
  formApi: AnyFormApi,
  isPrimaryAddressSelectionEnabled = false,
) {
  const isMailingAddressLinkedToPrimary = Boolean(
    formApi.getFieldValue(SAME_AS_PRIMARY_FIELD),
  );

  if (
    !isMailingAddressLinkedToPrimary ||
    !isPrimaryAddressField(fieldApi.name, isPrimaryAddressSelectionEnabled)
  ) {
    return;
  }

  syncMailingAddressWithPrimary(formApi);
}

export function primaryAddressCastToBooleanListener(
  fieldApi: AnyFieldApi,
  formApi: AnyFormApi,
) {
  if (typeof fieldApi.state.value === 'string') {
    const booleanValue = fieldApi.state.value === 'true';
    setFieldValueIfChanged(formApi, fieldApi.name, booleanValue);
  }
}
